/**
 * <ZoomPanelPreJoin>
 *
 * Page-mode pre-join slot for `<ZoomPanel>`. Rendered in place of `null` when
 * the doctor has landed on `/call/:encounterId` (e.g. by clicking a scheduled
 * visit) but no Zoom call is live yet. It occupies the SAME fixed footprint as
 * the collapsed (rail) live panel — same `top`/`right`/`height` anchors and the
 * same `--zoom-panel-width` reservation published by `<ZoomPanel>` — so the
 * chart reflows identically whether the call is live or merely about to start.
 *
 * Auto-join on landing was deliberately removed (a schedule click must not
 * silently start Zoom), so entry is this explicit "Join Session" CTA. Clicking
 * it calls back into CallPage's single `requestJoin` entry (recovery probe →
 * fresh/mock join).
 *
 * The `connecting` variant swaps the CTA for a spinner while a join is in
 * flight (Zoom SDK chunk download → signature fetch → init/join handshake —
 * several seconds), so the rail is visible from the moment the join starts
 * rather than only after it lands.
 *
 * Intentionally tiny + presentational — keeps the 900-line `<ZoomPanel>` from
 * growing and has no Zoom/scribe dependencies of its own.
 */
import { IconLoader2, IconVideo } from '@tabler/icons-react'
import {
  SCRIBE_LANGUAGE_LABELS,
  SCRIBE_SOURCE_LANGUAGES,
  type ScribeSourceLanguage,
} from '@behuman/core-contract'
import { CHART_CONTENT_HEIGHT_VAR, CHART_CONTENT_TOP_VAR } from '@/pages/Patient/chartTopOffset'
import { initialsOf } from './initials'

export type ZoomPanelPreJoinProps = {
  /** Panel width (px) — matches the live rail's `effectiveWidth` so the slot lines up. */
  width: number
  /** Patient display name shown under the avatar. Falls back to "Patient". */
  patientName?: string
  /** Visit type subtitle (e.g. "Virtual Visit"). */
  visitType?: string
  /** Click handler for the "Join Session" CTA. No-op when absent. */
  onJoin?: () => void
  /** Join-in-flight: replaces the CTA with a spinner + "Connecting…" line. */
  connecting?: boolean
  /** PD-1129 backup method — open the empty note without joining. Button hidden when absent (e.g. managers). */
  onWriteManualNote?: () => void
  /** PD-1600 — the language the visit will be spoken in; the picker renders only with `onLanguageChange`. */
  language?: ScribeSourceLanguage
  onLanguageChange?: (language: ScribeSourceLanguage) => void
}

export function ZoomPanelPreJoin({
  width,
  patientName,
  visitType,
  onJoin,
  connecting,
  onWriteManualNote,
  language = 'en',
  onLanguageChange,
}: ZoomPanelPreJoinProps) {
  const displayName = patientName ?? 'Patient'
  const initials = initialsOf(patientName) || 'PT'

  return (
    <aside
      data-testid="zoom-panel-prejoin"
      // Mirror the collapsed live panel's fixed slot: hug the right edge below
      // the chart's sticky chrome, height matches the chart content area. The
      // chart reserves matching padding-right via `--zoom-panel-width` (set by
      // <ZoomPanel> while this is showing) so nothing overlaps.
      className="fixed flex flex-col items-center justify-center gap-5 overflow-hidden bg-[#0f172a] px-6 text-center text-slate-200"
      style={{
        top: CHART_CONTENT_TOP_VAR,
        right: 0,
        width: `${width}px`,
        height: CHART_CONTENT_HEIGHT_VAR,
        zIndex: 'var(--z-scribe)' as unknown as number,
        borderLeft: '1px solid #1e293b',
      }}
    >
      <div
        className={
          'flex h-20 w-20 items-center justify-center rounded-full border-2 border-white/10 text-2xl font-bold text-[#e2e8f0]' +
          (connecting ? ' animate-pulse' : '')
        }
        style={{ background: 'linear-gradient(135deg, #475569, #334155)' }}
      >
        {initials}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-base font-semibold text-slate-100">{displayName}</span>
        <span className="text-[13px] text-slate-400">{visitType ?? 'Virtual Visit'}</span>
      </div>

      {connecting ? (
        <>
          <p className="max-w-[240px] text-[13px] leading-snug text-slate-500">
            Setting up your video session. This can take a few seconds.
          </p>
          <div
            role="status"
            data-testid="zoom-panel-connecting"
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-slate-200"
          >
            <IconLoader2 size={18} className="animate-spin" />
            Connecting…
          </div>
        </>
      ) : (
        <>
          <p className="max-w-[240px] text-[13px] leading-snug text-slate-500">
            Join to have the visit transcribed into a draft progress note
            {onWriteManualNote ? ', or write the note manually' : ''}.
          </p>
          {onLanguageChange && (
            <div className="flex flex-col items-center gap-1.5">
              <span className="text-[11px] tracking-wide text-slate-500 uppercase">
                Visit language
              </span>
              <div
                role="radiogroup"
                aria-label="Visit language"
                data-testid="zoom-panel-language"
                className="flex items-center gap-1 rounded-lg border border-white/15 p-1"
              >
                {SCRIBE_SOURCE_LANGUAGES.map((code) => (
                  <button
                    key={code}
                    type="button"
                    role="radio"
                    aria-checked={language === code}
                    data-testid={`zoom-panel-language-${code}`}
                    onClick={() => onLanguageChange(code)}
                    className={
                      'rounded-md px-3 py-1 text-[12px] font-semibold transition-colors ' +
                      (language === code
                        ? 'bg-white/15 text-slate-100'
                        : 'text-slate-400 hover:text-slate-200')
                    }
                  >
                    {SCRIBE_LANGUAGE_LABELS[code]}
                  </button>
                ))}
              </div>
              {language !== 'en' && (
                <span className="max-w-[240px] text-[11px] leading-snug text-slate-500">
                  Transcribed in {SCRIBE_LANGUAGE_LABELS[language]}; the note is written in English.
                </span>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => onJoin?.()}
            data-testid="zoom-panel-join-session"
            className="flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-colors"
            style={{ background: '#16a34a' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#15803d'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#16a34a'
            }}
          >
            <IconVideo size={18} />
            Join Session
          </button>
          {onWriteManualNote && (
            <button
              type="button"
              onClick={onWriteManualNote}
              data-testid="zoom-panel-write-manually"
              className="rounded-lg border border-white/15 bg-transparent px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:bg-white/5"
            >
              Write note manually
            </button>
          )}
        </>
      )}
    </aside>
  )
}
